// Portal "Hulp vragen aan collega" — gedeelde DTO's + Zod-query-schema + de
// vraagstuk→SkillCategory-mapping. Bron-spec: prompts/portal-hulp-vragen-aan-collega.md.
//
// Eén waarheid: de API valideert de query met `HulpCollegasQuerySchema` én de
// portal bouwt de querystring op dezelfde enum (HULP_VRAAGSTUKKEN). Geen tweede
// match-engine: het vraagstuk-pad rankt via de gedeelde effectief-niveau-helper
// (`bepaalEffectiefSkillNiveau`, ./hrm) op de mapped SkillCategory-skills.

import { z } from 'zod'
import type { SkillCategory } from './hrm'

/**
 * De vier knoppen op het hulp-scherm. KLANTSPECIFIEK heeft géén skill-categorie:
 * dat pad rankt op klant-ervaring (gewerkte dagen bij de klant) i.p.v. op skills.
 */
export const HULP_VRAAGSTUKKEN = ['ELEKTRO', 'SOFTWARE', 'MECHANISCH', 'KLANTSPECIFIEK'] as const
export type HulpVraagstuk = (typeof HULP_VRAAGSTUKKEN)[number]

export const HULP_VRAAGSTUK_LABELS: Record<HulpVraagstuk, string> = {
  ELEKTRO: 'Elektro',
  SOFTWARE: 'Software',
  MECHANISCH: 'Mechanisch',
  KLANTSPECIFIEK: 'Klantspecifiek',
}

/**
 * Vraagstuk → SkillCategory-mapping. EXPLICIET en op één plek, zodat dit later
 * een instelling kan worden (bv. een koppeling per vraagstuk in de DB) i.p.v.
 * vier hardcoded queries verspreid door de code. KLANTSPECIFIEK mapt bewust op
 * een lege lijst: dat vraagstuk gebruikt klant-historie, geen skills.
 */
export const VRAAGSTUK_SKILL_CATEGORIE_MAP: Record<HulpVraagstuk, SkillCategory[]> = {
  ELEKTRO: ['ELEKTRONICA', 'REGELTECHNIEK', 'MEETTECHNIEK'],
  SOFTWARE: ['SOFTWARE', 'PROGRAMMERING', 'AUTOMATISERING'],
  MECHANISCH: ['MECHANIEK', 'HYDRAULIEK', 'PNEUMATIEK'],
  KLANTSPECIFIEK: [],
}

/**
 * Status-vandaag categorie. Bewust grof: AFWEZIG dekt zowel verlof als verzuim
 * zónder onderscheid, zodat we geen verzuim-/verlofdetail lekken (AVG; spec
 * §Privacy "alleen 'niet beschikbaar'"). ONBEKEND alleen wanneer de status niet
 * af te leiden is.
 */
export const HULP_STATUS_VANDAAG = ['BESCHIKBAAR', 'INGEPLAND', 'AFWEZIG', 'ONBEKEND'] as const
export type HulpStatusVandaag = (typeof HULP_STATUS_VANDAAG)[number]

export const HULP_STATUS_VANDAAG_LABELS: Record<HulpStatusVandaag, string> = {
  BESCHIKBAAR: 'Beschikbaar',
  INGEPLAND: 'Ingepland',
  AFWEZIG: 'Niet beschikbaar',
  ONBEKEND: 'Onbekend',
}

/** Eén relevante skill van een collega binnen het gevraagde vraagstuk. */
export interface HulpCollegaSkillDTO {
  naam: string
  category: SkillCategory
  /** Effectief niveau (management ?? eigen ?? level) op de schaal van die skill. */
  niveau: number | null
}

/** Klant-ervaring van een collega bij de in-context klant (afgeleid, geen model). */
export interface HulpKlantErvaringDTO {
  /** Laatste keer dat de collega bij deze klant gewerkt heeft (ISO-datum). */
  laatsteKeer: string | null
  /** Aantal gewerkte dagen bij deze klant in de laatste 12 maanden. */
  dagenLaatste12Maanden: number
  /** Aantal gewerkte dagen bij deze klant in totaal. */
  dagenTotaal: number
}

export interface HulpCollegaDTO {
  employeeId: string
  naam: string
  functie: string | null
  /** Zakelijk telefoonnummer (Employee.phone). NOOIT het privénummer. */
  telefoon: string | null
  bv: string | null
  status: HulpStatusVandaag
  /**
   * True wanneer de collega vandaag bij de in-context klant is ingepland — een
   * relevant "die staat daar nu ook"-signaal. We tonen bewust géén andere
   * klantnaam bij INGEPLAND (privacy-minimalisatie: "ingepland elders").
   */
  ingeplandBijContextKlant: boolean
  /** Alleen gevuld wanneer er een klant in context is. */
  klantErvaring: HulpKlantErvaringDTO | null
  /** Hoogste relevante skills binnen het gevraagde vraagstuk (max 3, niveau desc). */
  relevanteSkills: HulpCollegaSkillDTO[]
}

export interface HulpCollegasResponseDTO {
  klant: { id: string; naam: string } | null
  vraagstuk: HulpVraagstuk | null
  collegas: HulpCollegaDTO[]
}

/**
 * Query voor GET /hulp/collegas. `werkbonId` is de primaire ingang (bepaalt de
 * klant); `clientId` is het alternatief vanaf het losse scherm. Beide optioneel:
 * een puur vraagstuk-zoek (zonder klant) mag ook. De route 404't niet op een
 * onbekende id — die levert simpelweg geen klant-context op.
 */
export const HulpCollegasQuerySchema = z.object({
  werkbonId: z.string().trim().min(1).optional(),
  clientId: z.string().trim().min(1).optional(),
  vraagstuk: z.enum(HULP_VRAAGSTUKKEN).optional(),
})
export type HulpCollegasQuery = z.infer<typeof HulpCollegasQuerySchema>

// ─── Collega-aanwezigheid bij de klant (FO-118) ───────────────────────────────
// Aparte feature naast /hulp/collegas: een tijd-roster van wie er +/-7 dagen rond
// de werkbon bij dezelfde klant staat of gestaan heeft, met tijden. Doel is
// coördineren (bellen/WhatsApp op het ZAKELIJKE nummer), niet skill-/ervaring-
// ranking. Privé-data blijft buiten beeld; besloten werkbonnen worden server-side
// afgeschermd (FO-008). Eén bron: de API valideert de query met
// `AanwezigheidQuerySchema`, de portal bouwt de querystring op hetzelfde schema.

/** Positie van een aanwezigheidsdag t.o.v. vandaag (Europe/Amsterdam). */
export const AANWEZIGHEID_TIJDSPOSITIES = ['EERDER', 'VANDAAG', 'AANKOMEND'] as const
export type AanwezigheidTijdspositie = (typeof AANWEZIGHEID_TIJDSPOSITIES)[number]

/** Eén collega op één dag bij de context-klant, binnen het +/-7-dagen-venster. */
export interface AanwezigheidEntryDTO {
  employeeId: string
  naam: string
  functie: string | null
  /**
   * Zakelijk telefoonnummer (Employee.phone). NOOIT het privénummer. `null`
   * wanneer de collega uit de bellijst is (zichtbaarInBellijst=false) of geen
   * nummer heeft: de UI toont dan geen bel-/WhatsApp-knop, wél de presentie.
   */
  telefoon: string | null
  bv: string | null
  /** Dag van aanwezigheid (ISO-datum 'yyyy-mm-dd', dag in Europe/Amsterdam). */
  datum: string
  tijdspositie: AanwezigheidTijdspositie
  /** Geplande tijden (HH:mm). */
  plannedStart: string | null
  plannedEnd: string | null
  /** Werkelijke tijden (HH:mm) indien geklokt; anders null. */
  actualStart: string | null
  actualEnd: string | null
  /** Klant-locatie van die werkbon-dag; null wanneer onbekend. */
  locatie: { id: string; naam: string } | null
  /** True wanneer dezelfde klant-locatie als de context-werkbon (anker). */
  zelfdeLocatie: boolean
  /** Werkbonnummer van de collega-werkbon (context-subtekst). */
  werkbonNummer: string
}

export interface AanwezigheidResponseDTO {
  klant: { id: string; naam: string } | null
  /** Anker-locatie = klant-locatie van de context-werkbon; null = onbekend (geen split). */
  ankerLocatie: { id: string; naam: string } | null
  /** Het toegepaste venster (ISO-datums, Europe/Amsterdam-dagen). */
  venster: { van: string; tot: string }
  entries: AanwezigheidEntryDTO[]
}

/**
 * Query voor GET /hulp/aanwezigheid. `werkbonId` is verplicht: de werkbon bepaalt
 * de klant, de anker-locatie en het +/-7-dagen-venster. Een onbekende id levert
 * een lege response op (de route 404't niet).
 */
export const AanwezigheidQuerySchema = z.object({
  werkbonId: z.string().trim().min(1),
})
export type AanwezigheidQuery = z.infer<typeof AanwezigheidQuerySchema>
